import { PackageIcon, X } from "lucide-react";
import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";

type DropzoneFileProps = {
  files: File[];
  setFiles: (files: File[]) => void;
  maxFiles?: number;
};

const DropzoneFile: React.FC<DropzoneFileProps> = ({
  files,
  setFiles,
  maxFiles = 1,
}) => {
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length + files.length > maxFiles) {
        setErrMsg(`You can only upload up to ${maxFiles} file(s).`);
        return;
      }

      const newFiles = acceptedFiles.filter(
        (newFile) =>
          !files.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
              existingFile.size === newFile.size
          )
      );
      setErrMsg(null);
      setFiles([...files, ...newFiles]);
    },
    [files, maxFiles, setFiles]
  );

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: maxFiles > 1,
    accept: { "image/*": [] },
  });

  return (
    <div
      className="cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-all duration-200 ease-in-out hover:border-blue-500 hover:bg-blue-50"
      {...getRootProps()}
    >
      <input {...getInputProps()} />

      <PackageIcon className="mx-auto h-12 w-12 text-gray-400" />

      <p className="text-gray-500">
        {isDragActive
          ? "Drop the image here..."
          : `Drag & drop an image here, or click to select (Max ${maxFiles})`}
      </p>

      {errMsg && <p className="mt-2 text-red-500">{errMsg}</p>}

      {files.length > 0 && (
        <ul className="mt-4 text-gray-700">
          {files.map((file, index) => (
            <li
              key={file.name}
              className="my-1 flex items-center justify-between rounded-md bg-gray-100 p-2"
            >
              <span className="text-left text-sm">
                {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </span>
              <button
                className="rounded-full p-1 text-red-500 hover:bg-red-100"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
              >
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DropzoneFile;
